// =============================================
// TagInput — type tags; the account's own tags are suggested as typing starts.
// =============================================
// Value in and out is the stored form: a comma list ("rental, 118 1SNW").
// Suggestions come from the account's tag dictionary (lib/tagStore — every tag used
// anywhere in the account: own books, family, businesses, clients). A name that is
// not there yet is offered as "Create tag …" and joins the dictionary once the
// record is saved (the caller runs recordTagUse after its save).
//
// Keyboard (sitewide rules): typing opens the list; ↑/↓ move; Enter or "," adds the
// highlighted / typed tag and stays in the box; Tab adds it and moves on; Backspace in
// an empty box removes the last tag; Esc closes the list. The X shows only while the
// box has text in it.
// =============================================

import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { parseTags, joinTags, subscribeTags, TagEntry, MAX_TAG_LEN } from '../../lib/tagStore';
import './TagInput.css';

interface TagInputProps {
  value: string | null | undefined;
  onChange: (value: string) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

const MAX_SUGGESTIONS = 8;

const TagInput: React.FC<TagInputProps> = ({ value, onChange, id, placeholder = 'Type a tag…', disabled, className, ariaLabel }) => {
  const tags = useMemo(() => parseTags(value), [value]);
  const [dictionary, setDictionary] = useState<TagEntry[]>([]);
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  // The box's text as of now — Tab adds the tag and then blurs before React re-renders,
  // so the blur must not add the old text a second time.
  const textRef = useRef('');
  const setBoxText = (v: string) => { textRef.current = v; setText(v); };

  useEffect(() => subscribeTags(setDictionary), []);

  const typed = text.trim().replace(/\s+/g, ' ').slice(0, MAX_TAG_LEN);

  // What to offer for the text typed: the account's tags that start with it, then
  // those with a word starting with it, then any containing it (most used first in
  // each), never one already on the record — and "Create" when it is a new name.
  const options = useMemo(() => {
    if (!typed) return [] as { name: string; isNew: boolean }[];
    const q = typed.toLowerCase();
    const chosen = new Set(tags.map(t => t.toLowerCase()));
    const rank = (n: string) => {
      const l = n.toLowerCase();
      if (l.startsWith(q)) return 0;
      if (l.split(/[\s\-_/]+/).some(w => w.startsWith(q))) return 1;
      return l.includes(q) ? 2 : 3;
    };
    const hits = dictionary
      .filter(t => !chosen.has(t.tag_name.toLowerCase()) && rank(t.tag_name) < 3)
      .sort((a, b) => rank(a.tag_name) - rank(b.tag_name) || b.use_count - a.use_count || a.tag_name.localeCompare(b.tag_name))
      .slice(0, MAX_SUGGESTIONS)
      .map(t => ({ name: t.tag_name, isNew: false }));
    const exists = dictionary.some(t => t.tag_name.toLowerCase() === q);
    if (!exists && !chosen.has(q)) hits.push({ name: typed, isNew: true });
    return hits;
  }, [typed, dictionary, tags]);

  useEffect(() => { setHighlight(0); }, [typed]);

  const add = (name: string) => {
    const clean = name.trim().replace(/\s+/g, ' ').slice(0, MAX_TAG_LEN);
    setBoxText('');
    setOpen(false);
    if (!clean || tags.some(t => t.toLowerCase() === clean.toLowerCase())) return;
    // a name the account already uses keeps the spelling it was first used with
    const known = dictionary.find(t => t.tag_name.toLowerCase() === clean.toLowerCase());
    onChange(joinTags([...tags, known ? known.tag_name : clean]) || '');
  };

  const remove = (name: string) => {
    onChange(joinTags(tags.filter(t => t !== name)) || '');
  };

  // the highlighted suggestion, else the text as typed
  const commit = () => {
    if (!typed) return;
    add(open && options[highlight] ? options[highlight].name : typed);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && options.length) {
      e.preventDefault(); setOpen(true); setHighlight(h => Math.min(h + 1, options.length - 1));
    } else if (e.key === 'ArrowUp' && options.length) {
      e.preventDefault(); setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (typed) { e.preventDefault(); commit(); }       // never submits the form while a tag is being typed
    } else if (e.key === ',') {
      e.preventDefault(); if (typed) add(typed);
    } else if (e.key === 'Tab') {
      if (typed) commit();                                // and focus moves on as usual
    } else if (e.key === 'Escape') {
      if (open) { e.preventDefault(); e.stopPropagation(); setOpen(false); }
    } else if (e.key === 'Backspace' && !text && tags.length) {
      remove(tags[tags.length - 1]);
    }
  };

  return (
    <div className={`ti-wrap ${className || ''}`}>
      <div className={`ti-box ${disabled ? 'ti-disabled' : ''}`} onMouseDown={e => {
        if (e.target === e.currentTarget) { e.preventDefault(); inputRef.current?.focus(); }
      }}>
        {tags.map(t => (
          <span key={t} className="ti-chip">
            {t}
            {!disabled && (
              <button type="button" tabIndex={-1} className="ti-chip-x" title={`Remove tag ${t}`} aria-label={`Remove tag ${t}`}
                onMouseDown={e => e.preventDefault()} onClick={() => remove(t)}>
                <FiX size={12} />
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          className="ti-input"
          type="text"
          value={text}
          disabled={disabled}
          maxLength={MAX_TAG_LEN}
          placeholder={tags.length ? '' : placeholder}
          aria-label={ariaLabel || 'Tags'}
          role="combobox"
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded={open && options.length > 0}
          onChange={e => { setBoxText(e.target.value); setOpen(!!e.target.value.trim()); }}
          onKeyDown={onKeyDown}
          // leaving the box keeps what was typed as a tag rather than losing it
          onBlur={() => { if (textRef.current.trim()) add(textRef.current); else setOpen(false); }}
        />
        {/* nothing typed = nothing to clear */}
        {text && !disabled && (
          <button type="button" tabIndex={-1} className="ti-clear" title="Clear" aria-label="Clear"
            onMouseDown={e => e.preventDefault()} onClick={() => { setBoxText(''); setOpen(false); inputRef.current?.focus(); }}>
            <FiX size={14} />
          </button>
        )}
      </div>
      {open && options.length > 0 && (
        <ul id={listId} className="ti-menu" role="listbox">
          {options.map((o, i) => (
            <li
              key={`${o.isNew ? 'new:' : ''}${o.name}`}
              role="option"
              aria-selected={i === highlight}
              className={`ti-option ${i === highlight ? 'ti-option-hl' : ''} ${o.isNew ? 'ti-option-new' : ''}`}
              onMouseDown={e => { e.preventDefault(); add(o.name); }}
              onMouseEnter={() => setHighlight(i)}
            >
              {o.isNew ? <>Create tag “<strong>{o.name}</strong>”</> : o.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TagInput;
